import { useState } from 'react';
import { Card, Tabs, Button, Space, Select, Switch, Skeleton, Segmented, Typography, Tag, Row, Col, theme, App } from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, DeleteOutlined, PictureOutlined, TranslationOutlined,
  EditOutlined, ProfileOutlined, DatabaseOutlined, LinkOutlined, FileOutlined, SettingOutlined,
} from '@ant-design/icons';
import { SectionCard } from '@/components/SectionCard';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTopic, useUpdateTopic, useCreateTopic } from '@/hooks/useTopics';
import { useLanguages } from '@/hooks/useLanguages';
import { useTopicTypes } from '@/api/topicTypes';
import { useDisplaySections } from '@/api/displaySections';
import { PerLanguageTab } from './PerLanguageTab';
import { StructuredDataTab } from './StructuredDataTab';
import { AttachmentsTab } from './AttachmentsTab';
import { LinksTab } from './LinksTab';
import { ExtractFromImageModal } from './ExtractFromImageModal';
import { ImportFromTextModal } from './ImportFromTextModal';
import { TranslateAllModal } from '@/components/TranslateAllModal';
import { useConfirmDeleteTopic } from './DeleteTopicConfirm';
import {
  writeStructured,
  readBlobKind,
  langHasContent,
  normalizeStructured,
  type Topic,
  type TopicDetails,
  type ContentLink,
} from '@/types/topic';
import { migrateFlatToProgram } from '@/types/programStructured';
import { showStructuredTab } from './structured/kinds';
import { ContentFieldsTab } from '@/pages/Content/ContentFieldsTab';
import { allowedTypeIdsForSection, fieldKindsForType } from '@/pages/Content/contentModel';
import type { ExtractedDraft } from '@/types/extractedTopic';

export function TopicEditPage() {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const sectionParam = search.get('section');
  const sectionId = sectionParam ? Number(sectionParam) : null;
  const isNew = !id || id === 'new';
  const nav = useNavigate();
  const topicQ = useTopic(isNew ? null : id!);
  const langsQ = useLanguages();
  const typesQ = useTopicTypes();
  const secQ = useDisplaySections();
  const createMut = useCreateTopic();
  const updateMut = useUpdateTopic();
  const confirmDel = useConfirmDeleteTopic();

  const [typeId, setTypeId] = useState<number>(3);
  const [status, setStatus] = useState<'draft' | 'active'>('draft');
  const [details, setDetails] = useState<TopicDetails>({
    title: { en: '' },
    content: { text: { en: '' } },
  });
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [activeLang, setActiveLang] = useState('en');
  const [extractOpen, setExtractOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  // Languages to pre-check in the translate picker: undefined = all eligible
  // (top "Translate…" button), or a subset (Basic content's per-language button).
  const [translateDefault, setTranslateDefault] = useState<string[] | undefined>(undefined);

  const openTranslate = (defaults?: string[]) => {
    setTranslateDefault(defaults);
    setTranslateOpen(true);
  };

  const [hydratedId, setHydratedId] = useState<string | null>(null);
  if (!isNew && topicQ.data && hydratedId !== topicQ.data.id) {
    const t = topicQ.data;
    setHydratedId(t.id);
    setTypeId(t.type_id);
    setStatus(t.status === 'archived' ? 'draft' : t.status);
    setDetails(t.details ?? {});
    setSelectedTags((t.display_sections ?? []).map((s) => s.id));
  }

  // A new topic opened from the Content Center is scoped to a section: pre-select
  // that display tag and default the type to the section's first allowed type.
  const [scopedInit, setScopedInit] = useState(false);
  if (isNew && sectionId && !scopedInit && secQ.data) {
    setScopedInit(true);
    const allowed = allowedTypeIdsForSection(secQ.data, sectionId);
    if (allowed.length) setTypeId(allowed[0]);
    setSelectedTags([sectionId]);
  }

  // After a background translate job writes to the DB, pull the fresh row and
  // re-run the hydration path (reset the guard) so the per-language boxes show
  // the new translations — the editor holds a local copy hydrated on load that
  // would otherwise stay stale until a manual page reload.
  async function reloadDetails() {
    await topicQ.refetch();
    setHydratedId(null);
  }

  const structuredVisible = showStructuredTab(typeId, readBlobKind(details));

  const langs = (langsQ.data ?? []).filter((l) => l.enabled);
  const langCodes = langs.map((l) => l.code);

  // Guard the active language against the enabled set (e.g. a language was
  // disabled since it was selected). Fall back to en, else the first enabled.
  const curLang = langCodes.includes(activeLang) ? activeLang : (langCodes.includes('en') ? 'en' : langCodes[0] ?? 'en');

  // Language switcher options with a completeness dot (green = has title/body).
  const langOptions = langs.map((l) => ({
    value: l.code,
    label: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ color: langHasContent(details, l.code) ? '#52c41a' : '#ccc', fontSize: 10 }}>●</span>
        {l.code}
        {l.code === 'en' ? <span style={{ color: '#aaa', fontSize: 11 }}>(source)</span> : null}
      </span>
    ),
  }));
  const eligible = langCodes.filter((c) => c !== 'en');
  const translatedCount = eligible.filter((c) => langHasContent(details, c)).length;

  // Display-tag options: permitted for the current type, plus any already selected.
  const tagOptions = (secQ.data ?? [])
    .filter((s) => s.allowed_type_ids.includes(typeId) || selectedTags.includes(s.id))
    .map((s) => ({ value: s.id, label: s.tag_name }));

  const typeSelectOptions = (typesQ.data ?? [])
    .filter((t) => !sectionId || allowedTypeIdsForSection(secQ.data ?? [], sectionId).includes(t.id))
    .map((t) => ({ value: t.id, label: t.name }));
  const typeName = (typesQ.data ?? []).find((t) => t.id === typeId)?.name ?? `Type ${typeId}`;

  const links: ContentLink[] = details.content?.links ?? [];
  const setLinks = (next: ContentLink[]) =>
    setDetails((prev) => {
      const d: TopicDetails = JSON.parse(JSON.stringify(prev));
      d.content = d.content ?? {};
      d.content.links = next;
      return d;
    });

  function applyDraft(draft: ExtractedDraft) {
    setDetails((prev) => {
      const d: TopicDetails = JSON.parse(JSON.stringify(prev));
      d.title = { ...(d.title ?? {}), en: draft.title.en ?? d.title?.en ?? '' };
      d.content = d.content ?? {};
      d.content.text = { ...(d.content.text ?? {}), en: draft.content_text.en ?? '' };
      // Tolerate a draft in either shape: new nested (from the updated extractor)
      // or the legacy flat blob (older extractor output) → migrate it.
      const raw = draft.structured as unknown as Record<string, unknown>;
      const s = 'eligibility' in raw || 'rate_scenarios' in raw
        ? normalizeStructured(raw)
        : migrateFlatToProgram(raw);
      return writeStructured(d, s);
    });
  }

  async function save() {
    const nameById = new Map((secQ.data ?? []).map((s) => [s.id, s.tag_name]));
    const existingById = new Map((topicQ.data?.display_sections ?? []).map((s) => [s.id, s.sort_index]));
    const display_sections = selectedTags.map((sid) => ({
      id: sid,
      tag_name: nameById.get(sid) ?? String(sid),
      sort_index: existingById.get(sid) ?? 999,
    }));
    const input = { type_id: typeId, status, details, display_sections };
    try {
      if (isNew) {
        const created = await createMut.mutateAsync(input);
        message.success('Topic created');
        nav(`/topics/${created.id}`, { replace: true });
      } else {
        await updateMut.mutateAsync({ id: id!, input });
        message.success('Saved');
      }
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  async function del() {
    if (isNew || !topicQ.data) return;
    if (await confirmDel(topicQ.data as Topic)) nav('/topics');
  }

  if (!isNew && topicQ.isLoading)
    return (
      <Card>
        <Skeleton active />
      </Card>
    );

  const fieldLabel = (text: string) => (
    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{text}</Typography.Text>
  );

  const tabItems = [
    {
      key: '__basic__',
      label: <span><EditOutlined /> Basic content</span>,
      children: (
        <PerLanguageTab
          lang={curLang}
          isSource={curLang === 'en'}
          topicId={isNew ? undefined : id}
          onTranslate={() => openTranslate([curLang])}
          title={details.title?.[curLang] ?? ''}
          content={details.content?.text?.[curLang] ?? ''}
          onChange={({ title, content }) => {
            const next: TopicDetails = JSON.parse(JSON.stringify(details));
            next.title ??= {};
            next.title[curLang] = title;
            next.content ??= {};
            next.content.text ??= {};
            next.content.text[curLang] = content;
            setDetails(next);
          }}
        />
      ),
    },
    ...(fieldKindsForType(typeId).length
      ? [{
          key: '__content__',
          label: <span><ProfileOutlined /> Content fields</span>,
          children: <ContentFieldsTab typeId={typeId} details={details} langs={[curLang]} onChange={setDetails} />,
        }]
      : []),
    {
      key: '__links__',
      label: <span><LinkOutlined /> Links</span>,
      children: <LinksTab value={links} langs={[curLang]} onChange={setLinks} />,
    },
    ...(structuredVisible
      ? [{
          key: '__structured__',
          label: <span><DatabaseOutlined /> Structured data</span>,
          children: <StructuredDataTab typeId={typeId} details={details} lang={curLang} onChange={setDetails} />,
        }]
      : []),
    {
      key: '__attachments__',
      label: <span><FileOutlined /> Attachments</span>,
      children: (
        <AttachmentsTab
          attachments={topicQ.data?.attachments ?? []}
          onRemove={() => message.info('Attachment removal not yet implemented')}
        />
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1100 }}>
      {/* Zone 1 — page header: identity + primary actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav('/topics')}>Back</Button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Space align="center" size={8} wrap>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {isNew ? 'New Topic' : (details.title?.en || `Topic ${id}`)}
            </Typography.Title>
            <Tag color="geekblue">{typeName}</Tag>
            <Tag color={status === 'active' ? 'success' : 'gold'}>{status === 'active' ? 'Active' : 'Draft'}</Tag>
          </Space>
        </div>
        <Space>
          {!isNew && <Button danger icon={<DeleteOutlined />} onClick={del}>Delete</Button>}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={save}
            loading={createMut.isPending || updateMut.isPending}
          >
            {isNew ? 'Create' : 'Save'}
          </Button>
        </Space>
      </div>

      {/* Zone 2 — Settings: what this topic is + where it appears */}
      <SectionCard tone="slate" icon={<SettingOutlined />} title="Settings" desc="Type, status, and where this topic appears">
        <Row gutter={[16, 12]} align="bottom">
          <Col xs={24} md={7}>
            {fieldLabel('Type')}
            <Select value={typeId} onChange={setTypeId} options={typeSelectOptions} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} md={11}>
            {fieldLabel('Display tags')}
            <Select
              mode="multiple" allowClear placeholder="Where this topic appears"
              value={selectedTags} onChange={setSelectedTags} options={tagOptions} style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={6}>
            {fieldLabel('Status')}
            <Switch
              checked={status === 'active'}
              checkedChildren="Active" unCheckedChildren="Draft"
              onChange={(v) => setStatus(v ? 'active' : 'draft')}
            />
          </Col>
        </Row>
      </SectionCard>

      {/* Zone 3 — Content: language switcher, AI tools, and the editor tabs */}
      <SectionCard
        tone="sky"
        icon={<EditOutlined />}
        title="Content"
        desc="Edit the topic's title, body, and structured fields per language"
        actions={
          <Space wrap>
            <Button size="small" icon={<PictureOutlined />} onClick={() => setExtractOpen(true)}>Extract from image</Button>
            <Button size="small" icon={<ProfileOutlined />} onClick={() => setImportOpen(true)}>Import text/JSON</Button>
            {!isNew && (
              <Button size="small" icon={<TranslationOutlined />} onClick={() => openTranslate(undefined)}>Translate…</Button>
            )}
          </Space>
        }
      >
        {/* Language switcher bar — data-driven; green dot = translated. */}
        {langs.length > 0 && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 14px', marginBottom: 16, borderRadius: 8,
              background: token.colorFillQuaternary, border: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <Typography.Text strong style={{ fontSize: 13 }}>Language</Typography.Text>
            <Segmented value={curLang} onChange={(v) => setActiveLang(v as string)} options={langOptions} />
            <Tag color="blue" style={{ marginInlineStart: 'auto', marginInlineEnd: 0 }}>
              {translatedCount}/{eligible.length} translated
            </Tag>
          </div>
        )}

        <Tabs defaultActiveKey="__basic__" items={tabItems} />
      </SectionCard>

      <ExtractFromImageModal open={extractOpen} onClose={() => setExtractOpen(false)} onExtracted={applyDraft} />
      <ImportFromTextModal open={importOpen} onClose={() => setImportOpen(false)} onExtracted={applyDraft} />
      {!isNew && (
        <TranslateAllModal
          open={translateOpen}
          topicId={id!}
          details={details}
          defaultTargets={translateDefault}
          onTranslated={reloadDetails}
          onClose={() => setTranslateOpen(false)}
        />
      )}
    </div>
  );
}
